// ============================================================
// promptBox.js — Controlador del cuadro de prompt
// ============================================================
import { qs, qsa, on } from './utils.js';
import { toast } from './toast.js';
import { openCreationsModal } from './creationsModal.js';
import { uploadAttachment } from './api.js';

/**
 * Estado del prompt — se exporta para que el backend pueda
 * leerlo o se pueda serializar antes de enviarlo a la API de IA.
 */
const state = {
  type: 'image',           // 'image' | 'video' | 'avatar'
  prompt: '',
  model: 'nano-banana-2',
  aspect: '16:9',
  quality: '2K',
  count: 1,
  duration: 5,
  audio: true,
  attachments: [],
};

export function getPromptState() {
  const attachments = [];
  qsa('.prompt-attach-chip.has-file').forEach(chip => {
    if (chip._file) {
      attachments.push(chip._file);
    }
  });
  return {
    ...state,
    attachments,
  };
}

export function initPromptBox() {
  qsa('.prompt-box').forEach(setupPromptBox);
}

function setupPromptBox(box) {
  state.type = box.dataset.promptType || 'image';

  // Sincronizar el modelo inicial según el tipo y los elementos activos del DOM
  const activeModelItem = qs('.model-item.is-active', box);
  if (activeModelItem && activeModelItem.dataset.model) {
    state.model = activeModelItem.dataset.model;
  } else if (state.type === 'video') {
    state.model = 'kling-3.0';
  } else {
    state.model = 'nano-banana-2';
  }

  // Textarea — bind al estado y auto-grow
  const textarea = qs('.prompt-textarea', box);
  if (textarea) {
    textarea.addEventListener('input', () => {
      state.prompt = textarea.value;
      autoGrow(textarea);
    });
  }

  // Selectores de opciones (forma, calidad, cantidad)
  on(box, 'click', '.option-tile', (e, tile) => {
    const group = tile.parentElement.dataset.group;
    if (!group) return;
    qsa('.option-tile', tile.parentElement).forEach((t) => t.classList.remove('is-active'));
    tile.classList.add('is-active');
    const value = tile.dataset.value;

    if (group === 'aspect')   state.aspect = value;
    if (group === 'quality')  state.quality = value;
    if (group === 'count')    state.count = parseInt(value, 10);

    syncSummary(box);
  });

  // Selección de modelo
  on(box, 'click', '.model-item[data-model]', (e, item) => {
    const list = item.closest('.model-list');
    if (!list) return;
    qsa('.model-item', list).forEach((i) => i.classList.remove('is-active'));
    item.classList.add('is-active');
    state.model = item.dataset.model;
    const name = qs('.model-name', item)?.textContent.trim();
    qsa('[data-bind="currentModel"]', box).forEach((n) => (n.textContent = name));
  });

  // Audio toggle (solo en vídeo)
  const audio = qs('#audioToggle', box);
  if (audio) {
    state.audio = audio.checked;
    audio.addEventListener('change', (e) => {
      state.audio = e.target.checked;
      syncSummary(box);
    });
  }

  // Slider de duración (solo en vídeo)
  const duration = qs('#durationRange', box);
  const durationLabel = qs('#durationValue', box);
  duration?.addEventListener('input', (e) => {
    state.duration = parseInt(e.target.value, 10);
    if (durationLabel) durationLabel.textContent = state.duration;
    syncSummary(box);
  });

  // Quitar adjunto:
  //   - chip permanente (data-attach-permanent): limpia el archivo y vuelve al
  //     placeholder, pero el chip sigue visible.
  //   - chip normal: se oculta.
  on(box, 'click', '[data-action="attach-remove"]', (e, btn) => {
    e.stopPropagation();
    const chip = btn.closest('.prompt-attach-chip');
    if (!chip) return;
    chip._file = null;
    chip.classList.remove('has-file');
    resetChipLabel(chip);
    if (!chip.hasAttribute('data-attach-permanent')) {
      chip.classList.add('is-hidden');
    }
    updateUploadCounter(box);
  });

  // Añadir adjunto (botón +) → restaura el primer chip oculto o usa cualquiera
  on(box, 'click', '[data-action="attach-add"]', () => {
    const targetChip = qs('.prompt-attach-chip.is-hidden', box)
                    || qs('.prompt-attach-chip', box);
    openFilePicker(box, targetChip);
  });

  // Menú del chip: Subir imagen → afecta al chip que abrió el menú
  on(box, 'click', '[data-action="attach-upload"]', (e, btn) => {
    const chip = btn.closest('.prompt-attach-chip');
    openFilePicker(box, chip);
  });

  // Menú del chip: Mis creaciones → abre el modal con creaciones recientes
  on(box, 'click', '[data-action="attach-creations"]', (e, btn) => {
    const chip = btn.closest('.prompt-attach-chip');
    openCreationsModal(chip);
  });

  // Mejorar prompt con IA (placeholder)
  on(box, 'click', '[data-action="enhance-prompt"]', () => {
    if (!state.prompt.trim()) {
      toast({ message: 'Escribe algo primero para mejorarlo', type: 'error' });
      return;
    }
    toast({ message: 'Mejorando con IA…', type: 'info' });
  });

  // Botón generar — emite un evento custom que el backend puede capturar
  on(box, 'click', '[data-action="generate"]', () => {
    if (!state.prompt.trim()) {
      toast({ message: 'Describe lo que quieres generar', type: 'error' });
      qs('.prompt-textarea', box)?.focus();
      return;
    }

    // Evento custom que el integrador (backend / API) puede escuchar
    const event = new CustomEvent('vimo:generate', {
      detail: getPromptState(),
      bubbles: true,
    });
    box.dispatchEvent(event);

    toast({
      message: `Generando ${state.type}…`,
      type: 'success',
    });

    console.log('[Vimo] Generate payload:', getPromptState());
  });
}

function autoGrow(el) {
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, 220) + 'px';
}

function updateUploadCounter(box) {
  const activeChips = qsa('.prompt-attach-chip.has-file', box);
  const count = activeChips.length;
  qsa('[data-bind="upload-count"]', box).forEach(counter => {
    counter.textContent = `${count}/14`;
  });
}

// Abre el file picker y, si se sube algo, rellena el chip indicado con el
// nombre del archivo (haciéndolo visible y marcándolo como has-file).
function openFilePicker(box, chip) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*,video/*';
  input.addEventListener('change', async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (chip) {
      chip._file = file;
      chip.classList.remove('is-hidden');
      chip.classList.add('has-file');

      // Miniatura — reemplaza el icono con un <img> de previsualización
      const icon = qs('.chip-main > i', chip) || qs('.chip-thumb', chip);
      if (icon) {
        const thumb = document.createElement('img');
        thumb.src = URL.createObjectURL(file);
        thumb.alt = '';
        thumb.className = 'chip-thumb';
        icon.replaceWith(thumb);
      }

      // Label: nombre del archivo truncado
      const label = qs('[data-bind="attach-label"]', chip);
      if (label) label.textContent = file.name;
    }

    updateUploadCounter(box);

    const shortName = file.name.length > 28 ? file.name.slice(0, 28) + '…' : file.name;
    toast({ message: `Adjunto añadido: ${shortName}`, type: 'info' });

    try {
      const res = await uploadAttachment(file);
      if (chip) chip._fileUrl = res.url;
      console.log('[PromptBox] File uploaded to backend:', res.url);
    } catch (err) {
      console.error('[PromptBox] Upload error:', err);
    }
  });
  input.click();
}

// Restaura el label y el icono por defecto del chip.
// Si había una miniatura, revoca su object URL para liberar memoria.
function resetChipLabel(chip) {
  const label = qs('[data-bind="attach-label"]', chip);
  if (label) label.textContent = chip.dataset.attachDefault || 'Imagen de referencia';

  const thumb = qs('.chip-thumb', chip);
  if (thumb) {
    if (thumb.src && thumb.src.startsWith('blob:')) {
      URL.revokeObjectURL(thumb.src);
    }
    const icon = document.createElement('i');
    icon.className = 'bi bi-image';
    thumb.replaceWith(icon);
  } else {
    const icon = qs('.chip-main > i', chip);
    if (icon) icon.className = 'bi bi-image';
  }
}

function syncSummary(box) {
  // Actualiza los textos resumen de la píldora de configuración
  qsa('[data-bind="aspect"]', box).forEach((n) => (n.textContent = state.aspect));
  qsa('[data-bind="quality"]', box).forEach((n) => (n.textContent = state.quality));
  qsa('[data-bind="count"]', box).forEach((n) => (n.textContent = state.count));
  qsa('[data-bind="duration"]', box).forEach((n) => (n.textContent = `${state.duration}s`));
  qsa('[data-bind="audioStatus"]', box).forEach((n) => {
    if (state.audio) {
      n.style.color = '#4ade80';
      n.innerHTML = '<i class="bi bi-volume-up-fill"></i> Con audio';
    } else {
      n.style.color = '#94a3b8';
      n.innerHTML = '<i class="bi bi-volume-mute"></i> Sin audio';
    }
  });
}
